<template>
	<div class="abiturient-my-request-view">
		<v-row>
			<v-breadcrumbs :items="breadcrumbs" divider="/"></v-breadcrumbs>
		</v-row>

		<v-fade-transition>
			<div v-if="isLoading" class="requests-loader" aria-busy="true" aria-live="polite">
				<v-row>
					<v-col lg="3" md="4" cols="12">
						<v-skeleton-loader type="card" class="request-skeleton-card mb-4"></v-skeleton-loader>
					</v-col>
					<v-col lg="9" md="8" cols="12">
						<v-alert
							v-for="n in 4"
							:key="'skeleton-' + n"
							prominent
							dense
							border="left"
							colored-border
							color="orange darken-1"
							class="mb-4 request-skeleton-alert"
							:style="{ animationDelay: (n - 1) * 0.1 + 's' }"
						>
							<v-skeleton-loader
								type="list-item-two-line, divider, list-item-two-line, divider, list-item-two-line"
								class="request-skeleton-loader"
							></v-skeleton-loader>
						</v-alert>
					</v-col>
				</v-row>
				<v-progress-linear
					indeterminate
					color="primary"
					rounded
					height="3"
					class="requests-loader__bar mt-2"
				></v-progress-linear>
			</div>
		</v-fade-transition>

		<v-fade-transition>
		<v-row v-if="!isLoading">
			<v-col lg="3" md="4" cols="12">
				<template v-if="canShowEdit">
					<v-btn
						block
						color="primary"
						class="mb-4"
						:to="`/abiturient/my-requests/${requestId}/edit`"
					>
						{{ $t('Редактировать') }}
					</v-btn>
				</template>
				<template v-if="canShowWithdraw">
					<v-btn block color="error" class="mb-4 withdraw-btn" @click.stop="withdrawDialog = true">
						{{ $t('Отозвать заявку') }}
					</v-btn>
					<v-dialog v-model="withdrawDialog" width="500" :persistent="actionIsLoading">
						<v-card>
							<v-card-title class="headline lighten-2">{{ $t('Отозвать заявку') }}</v-card-title>
							<v-card-text>
								<p class="mb-3">{{ $t('Вы уверены, что хотите отозвать заявку?') }}</p>
								<v-form class="form" ref="withdrawForm">
									<sign-ecp @update-ecp-file-data="ecpFileData = $event" @update-ecp-password="ecpPassword = $event" />
								</v-form>
							</v-card-text>
							<v-divider></v-divider>
							<v-card-actions>
								<v-spacer></v-spacer>
								<v-btn color="primary" text @click.stop="withdrawSubmit" :loading="actionIsLoading">
									{{ $t('Подписать') }}
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
					<v-divider class="my-6" />
				</template>
				<v-card v-if="Object.keys(requestInfo).length" elevation="2" class="mb-5 info-card">
					<v-card-title class="card-title-custom">{{ requestInfo.name }}</v-card-title>
					<v-card-text class="request-data card-text-custom pt-4">
						<v-row
							v-for="(item, itemKey) in requestInfo.values"
							:key="itemKey"
							class="info-row"
							no-gutters
						>
							<v-col cols="12" class="prop-title pb-1" :class="item.labelClass">{{ item.name }}</v-col>
							<v-col cols="12" class="prop-value" v-if="item.type == 'handbook' || item.type == 'boolean'">{{ item.value.name }}</v-col>
							<v-col cols="12" class="prop-value" v-else-if="item.type != 'file'">{{ item.value }}</v-col>
							<v-col cols="12" class="prop-value" v-else>
								<authenticated-file-link
									v-if="item.authenticated"
									:url="item.value"
								/>
								<a v-else :href="item.value" :download="item.file_name || item.fileName" target="_blank">
									{{ item.file_name || item.fileName }}
								</a>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
			<v-col lg="9" md="8" cols="12">
				<v-expansion-panels multiple v-model="panel" class="request-data">
					<v-expansion-panel v-for="(panelItem, panelKey) in requestDate" :key="panelKey">
						<v-expansion-panel-header>{{ panelItem.name }}</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="pt-2">
								<template v-for="(item, itemKey) in panelItem.values">
									<v-row :key="itemKey" class="detail-row" no-gutters>
										<v-col cols="12" sm="5" class="prop-title">{{ item.name }}</v-col>
										<v-col cols="12" sm="7" class="prop-value" v-if="item.type != 'file'">{{ item.value }}</v-col>
										<v-col cols="12" sm="7" class="prop-value" v-else>
											<authenticated-file-link
												v-if="item.authenticated"
												:url="item.value"
											/>
											<a v-else :href="item.value" :download="item.file_name" target="_blank">{{ item.file_name }}</a>
										</v-col>
									</v-row>

									<template v-if="item.childs">
										<template v-for="(childItem, childKey) in item.childs">
											<v-row :key="'child-' + childKey" class="detail-row">
												<v-col cols="12">
													<div class="mb-3"><b>{{ childItem.exam_name.value }}</b></div>
													<v-row class="detail-row" no-gutters>
														<v-col cols="12" sm="5" class="prop-title">{{ childItem.exam_date.name }}</v-col>
														<v-col cols="12" sm="7" class="prop-value">{{ childItem.exam_date.value }}</v-col>
													</v-row>
													<v-row class="detail-row" no-gutters>
														<v-col cols="12" sm="5" class="prop-title">{{ childItem.exam_address.name }}</v-col>
														<v-col cols="12" sm="7" class="prop-value">{{ childItem.exam_address.value }}</v-col>
													</v-row>
													<v-row v-if="childItem.exam_mark.value" class="detail-row" no-gutters>
														<v-col cols="12" sm="5" class="prop-title">{{ childItem.exam_mark.name }}</v-col>
														<v-col cols="12" sm="7" class="prop-value">{{ childItem.exam_mark.value }}</v-col>
													</v-row>
												</v-col>
											</v-row>
										</template>
									</template>
								</template>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>
				</v-expansion-panels>
			</v-col>
		</v-row>
		</v-fade-transition>
	</div>
</template>

<script>
	import AuthenticatedFileLink from '@/components/abiturient/AuthenticatedFileLink.vue';
	import SignEcp from '@/components/SignEcp.vue';
	import { mapInfoOrderToView, normalizeRegionListResponse, normalizeRegions } from '@/utils/metadata';
	import { applyUploadUrlsToView, fetchOrderUploadUrls } from '@/utils/uploads';

	export default {
		components: {
			AuthenticatedFileLink,
			SignEcp,
		},
		mounted() {
			this.getData();
		},
		computed: {
			usesNewApi() {
				return this.$store.getters.usesAbiturientNewApi;
			},
			canShowEdit() {
				return this.usesNewApi && this.canEdit && this.requestId;
			},
			canShowWithdraw() {
				if (!Object.keys(this.requestInfo).length) {
					return false;
				}
				if (this.usesNewApi) {
					return this.canWithdraw;
				}
				const values = this.requestInfo.values;
				if (!values.from_site || !values.request_status) {
					return false;
				}
				return (values.from_site.value != 'EGOV' && +values.request_status.value.id == 2)
					|| (+values.request_status.value.id != 5
						&& +values.request_status.value.id != 6
						&& +values.request_status.value.id != 7);
			},
			breadcrumbs() {
				return [
					{
						text: this.$t('Главная'),
						disabled: false,
						exact: true,
						to: '/abiturient',
					},
					{
						text: this.$t('Мои заявки'),
						disabled: false,
						exact: true,
						to: '/abiturient/my-requests',
					},
					{
						text: this.$t('Просмотр заявки'),
						disabled: true,
						exact: true,
						to: '',
					},
				];
			},
		},
		data: () => ({
			isLoading: true,
			requestId: null,
			panel: [],
			actionIsLoading: false,
			withdrawDialog: false,
			requestDate: [],
			requestInfo: {},
			canWithdraw: false,
			canEdit: false,
			ecpFileData: null,
			ecpPassword: null,
		}),
		methods: {
			applyViewData(info, block) {
				this.requestInfo = info;
				this.requestDate = block;
				this.panel = block.map((_, index) => index);
			},
			async getData() {
				this.isLoading = true;
				try {
					if (this.usesNewApi) {
						const [orderResponse, regionsResponse] = await Promise.all([
							this.$http.get('/info-order', {
								params: { id: this.$route.params.id },
							}),
							this.$http.get('/regions'),
						]);
						const order = orderResponse.data.data || {};
						const regionList = normalizeRegions(normalizeRegionListResponse(regionsResponse));
						const mapped = mapInfoOrderToView(order, this.$t.bind(this), { regionList });
						try {
							const uploadUrls = await fetchOrderUploadUrls(this.$http, order);
							applyUploadUrlsToView(mapped.block, uploadUrls);
						} catch (uploadError) {
							console.error('Ошибка загрузки ссылок на документы:', uploadError);
						}
						this.applyViewData(mapped.info, mapped.block);
						this.canWithdraw = mapped.canWithdraw;
						this.canEdit = mapped.canEdit;
						this.requestId = order.id;
						return;
					}

					const response = await this.$http.get('/request/' + this.$route.params.id);
					const block = response.data.data.block;
					const blockList = Array.isArray(block) ? block : Object.values(block || {});
					this.applyViewData(response.data.data.info, blockList);
					this.canWithdraw = false;
					this.requestId = this.requestInfo.values.request_id.value;
				} catch (error) {
					console.error('Ошибка загрузки заявки:', error);
				} finally {
					this.isLoading = false;
				}
			},
			async withdrawSubmit() {
				if (!this.$refs.withdrawForm.validate()) {
					return;
				}

				this.actionIsLoading = true;
				try {
					let response;
					if (this.usesNewApi) {
						const formData = new FormData();
						formData.append('id', this.requestId);
						formData.append('ecp', this.ecpFileData);
						formData.append('password', this.ecpPassword);
						response = await this.$http.post('/recall', formData);
					} else {
						response = await this.$http.post('/withdraw', {
							p12: this.ecpFileData,
							password: this.ecpPassword,
							request_order_id: this.requestId,
						});
					}
					await this.getData();
					this.withdrawDialog = false;
					const message = (response.data && response.data.message)
						|| this.$t('Заявка успешно отозвана.');
					this.$store.dispatch('alertAdd', { text: message, type: 'success' });
				} catch (error) {
					console.error('Ошибка отзыва заявки:', error);
				} finally {
					this.actionIsLoading = false;
				}
			},
		},
		watch: {
			'$store.state.language': function () {
				this.getData();
			},
		},
	};
</script>

<style scoped src="@/assets/css/abiturient/MyRequestView.css">
</style>
